// src/components/Trading/MultiStrategyDashboard.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Stack,
  Chip,
  IconButton,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Switch,
  LinearProgress,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  alpha,
  useTheme,
  Tooltip,
  Grid,
  Card,
  CardContent,
  Divider,
  List,
  ListItem,
  Avatar,
  Tab,
  Tabs,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  CheckCircle as CheckIcon,
  RadioButtonUnchecked as UncheckedIcon,
  Warning as WarningIcon,
  Timeline as TimelineIcon,
  Close as CloseIcon,
  Circle as DotIcon,
  Assessment as AssessmentIcon,
  Schedule as ScheduleIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  NotificationsActive as SignalIcon,
  Info as InfoIcon,
  History as HistoryIcon,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';

const MultiStrategyDashboard = ({ apiBaseUrl = window.location.origin }) => {
  const theme = useTheme();

  // State
  const [strategies, setStrategies] = useState([]);
  const [filteredStrategies, setFilteredStrategies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [wsConnected, setWsConnected] = useState(false);
  const [selectedStrategy, setSelectedStrategy] = useState(null);
  const [signalHistory, setSignalHistory] = useState([]);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [strategyToDelete, setStrategyToDelete] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Refs
  const wsRef = useRef(null);

  useEffect(() => {
    loadStrategies();
  }, []);

  useEffect(() => {
    connectWebSocket();
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, []);

  useEffect(() => {
    if (!searchQuery) {
      setFilteredStrategies(strategies);
    } else {
      const filtered = strategies.filter(s => 
        s.strategy_id?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        s.symbol?.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredStrategies(filtered);
      setPage(0);
    }
  }, [strategies, searchQuery]);

  const connectWebSocket = () => {
    try {
      const wsUrl = apiBaseUrl.replace('http://', 'ws://').replace('https://', 'wss://');
      const ws = new WebSocket(`${wsUrl}/ws/strategy-alerts/`);

      ws.onopen = () => setWsConnected(true);

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          handleWebSocketUpdate(data);
        } catch (error) {
          console.error('Failed to parse WebSocket message:', error);
        }
      };

      ws.onerror = () => {};

      ws.onclose = () => {
        setWsConnected(false);
        setTimeout(connectWebSocket, 5000);
      };

      wsRef.current = ws;
    } catch (error) {
      console.error('Failed to connect WebSocket:', error);
    }
  };

  const handleWebSocketUpdate = useCallback((data) => {
    if (data.strategy_id) {
      setStrategies(prev => 
        prev.map(strategy => 
          strategy.strategy_id === data.strategy_id
            ? {
                ...strategy,
                lastSignal: data.signal_type,
                lastSignalTime: data.timestamp,
                totalSignals: (strategy.totalSignals || 0) + 1,
              }
            : strategy
        )
      );

      if (selectedStrategy?.strategy_id === data.strategy_id) {
        loadStrategyDetails(data.strategy_id);
      }
    }
  }, [selectedStrategy]);

  const loadStrategies = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${apiBaseUrl}/strategies/api/strategy/list/`);
      const data = await response.json();

      if (data.success && data.strategies) {
        const strategiesWithState = await Promise.all(
          data.strategies.map(async (strategy) => {
            try {
              const stateResponse = await fetch(
                `${apiBaseUrl}/strategies/api/strategy/state/${strategy.strategy_id}/`
              );
              const stateData = await stateResponse.json();
              
              return {
                ...strategy,
                state: stateData.success ? stateData.state : null,
                lastSignal: stateData.state?.last_signal_type || null,
                lastSignalTime: stateData.state?.last_signal_time || null,
                totalSignals: stateData.state?.total_signals_received || 0,
                entryConfirmed: stateData.state?.entry_confirmed || false,
              };
            } catch (error) {
              return strategy;
            }
          })
        );

        setStrategies(strategiesWithState);
      }
    } catch (error) {
      console.error('Failed to load strategies:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStrategyDetails = async (strategyId) => {
    try {
      setDetailLoading(true);
      
      // Fetch strategy state
      const stateResponse = await fetch(
        `${apiBaseUrl}/strategies/api/strategy/state/${strategyId}/`
      );
      const stateData = await stateResponse.json();
      
      // Fetch signal history
      const historyResponse = await fetch(
        `${apiBaseUrl}/strategies/api/signals/history/${strategyId}/?limit=50`
      );
      const historyData = await historyResponse.json();
      
      const strategy = strategies.find(s => s.strategy_id === strategyId);
      setSelectedStrategy({
        ...strategy,
        detailedState: stateData.success ? stateData.state : null,
      });
      
      setSignalHistory(historyData.success ? historyData.signals : []);
    } catch (error) {
      console.error('Failed to load strategy details:', error);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleRowClick = async (strategy) => {
    await loadStrategyDetails(strategy.strategy_id);
    setDetailDialogOpen(true);
  };

  const toggleStrategy = async (strategyId, isActive, event) => {
    event.stopPropagation();
    try {
      const response = await fetch(`${apiBaseUrl}/strategies/api/strategy/setup/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          strategy_id: strategyId,
          is_active: !isActive,
        }),
      });

      if (response.ok) {
        setStrategies(prev => 
          prev.map(s => 
            s.strategy_id === strategyId 
              ? { ...s, is_active: !isActive }
              : s
          )
        );
      }
    } catch (error) {
      console.error('Failed to toggle strategy:', error);
    }
  };

  const deleteStrategy = async (strategyId) => {
    try {
      const response = await fetch(
        `${apiBaseUrl}/strategies/api/strategy/delete/${strategyId}/`,
        { method: 'DELETE' }
      );

      if (response.ok) {
        setStrategies(prev => prev.filter(s => s.strategy_id !== strategyId));
        setDeleteDialogOpen(false);
        setStrategyToDelete(null);
      }
    } catch (error) {
      console.error('Failed to delete strategy:', error);
    }
  };

  const resetStrategy = async (strategyId, event) => {
    if (event) event.stopPropagation();
    try {
      const response = await fetch(`${apiBaseUrl}/strategies/api/strategy/reset/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ strategy_id: strategyId }),
      });

      if (response.ok) {
        loadStrategies();
        if (selectedStrategy?.strategy_id === strategyId) {
          loadStrategyDetails(strategyId);
        }
      }
    } catch (error) {
      console.error('Failed to reset strategy:', error);
    }
  };

  const getStatusColor = (strategy) => {
    if (!strategy.is_active) return '#666';
    if (strategy.entryConfirmed) return '#00ff88';
    if (strategy.lastSignal === 'early') return '#ffa502';
    return '#00d4ff';
  };

  const getStatusLabel = (strategy) => {
    if (!strategy.is_active) return 'Inactive';
    if (strategy.entryConfirmed) return 'Entry Confirmed';
    if (strategy.lastSignal === 'early') return 'Waiting';
    if (strategy.lastSignal === 'exit') return 'Exited';
    return 'Active';
  };

  const getConditionProgress = (strategy) => {
    if (!strategy.state) return 0;
    const conditions = Object.keys(strategy.conditions || {});
    if (conditions.length === 0) return 0;
    const metConditions = strategy.state.conditions_met || {};
    const met = Object.values(metConditions).filter(Boolean).length;
    return Math.round((met / conditions.length) * 100);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedStrategies = filteredStrategies.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Box sx={{ p: 3, minHeight: '100vh' }}>
      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          background: 'linear-gradient(135deg, rgba(15, 20, 40, 0.95) 0%, rgba(20, 25, 45, 0.95) 100%)',
          backdropFilter: 'blur(20px)',
          border: '1px solid',
          borderColor: alpha('#00d4ff', 0.2),
          borderRadius: 2,
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Avatar
              sx={{
                width: 48,
                height: 48,
                background: 'linear-gradient(135deg, #00d4ff 0%, #0088ff 100%)',
                boxShadow: '0 4px 16px rgba(0, 212, 255, 0.3)',
              }}
            >
              <DashboardIcon />
            </Avatar>
            <Box>
              <Typography variant="h5" sx={{ color: '#fff', fontWeight: 700, mb: 0.5 }}>
                Strategy Dashboard
              </Typography>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Chip
                  icon={<DotIcon />}
                  label={wsConnected ? 'Live' : 'Offline'}
                  size="small"
                  sx={{
                    bgcolor: wsConnected ? alpha('#00ff88', 0.2) : alpha('#ff4757', 0.2),
                    color: wsConnected ? '#00ff88' : '#ff4757',
                    '& .MuiChip-icon': {
                      color: wsConnected ? '#00ff88' : '#ff4757',
                      animation: wsConnected ? 'pulse 2s infinite' : 'none',
                    },
                    height: 24,
                  }}
                />
                <Typography variant="caption" sx={{ color: alpha('#fff', 0.6) }}>
                  {filteredStrategies.length} strategies • {filteredStrategies.filter(s => s.is_active).length} active
                </Typography>
              </Stack>
            </Box>
          </Stack>

          <Stack direction="row" spacing={1.5}>
            <TextField
              size="small"
              placeholder="Search strategies..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{
                width: 250,
                '& .MuiOutlinedInput-root': {
                  bgcolor: alpha('#000', 0.3),
                  '& fieldset': {
                    borderColor: alpha('#00d4ff', 0.2),
                  },
                  '&:hover fieldset': {
                    borderColor: alpha('#00d4ff', 0.4),
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#00d4ff',
                  },
                  '& input': {
                    color: '#fff',
                  },
                },
              }}
              InputProps={{
                startAdornment: <SearchIcon sx={{ color: alpha('#fff', 0.5), mr: 1, fontSize: 20 }} />,
              }}
            />
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={loadStrategies}
              sx={{
                borderColor: alpha('#00d4ff', 0.5),
                color: '#00d4ff',
                '&:hover': {
                  borderColor: '#00d4ff',
                  bgcolor: alpha('#00d4ff', 0.1),
                },
              }}
            >
              Refresh
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'Total', value: strategies.length, color: '#00d4ff', icon: <DashboardIcon /> },
          { label: 'Active', value: strategies.filter(s => s.is_active).length, color: '#00ff88', icon: <PlayIcon /> },
          { label: 'Entry Confirmed', value: strategies.filter(s => s.entryConfirmed).length, color: '#ffa502', icon: <CheckIcon /> },
          { label: 'Avg Signals', value: Math.round(strategies.reduce((acc, s) => acc + (s.totalSignals || 0), 0) / (strategies.length || 1)), color: '#764ba2', icon: <TimelineIcon /> },
        ].map((stat, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card
              sx={{
                background: `linear-gradient(135deg, ${alpha(stat.color, 0.1)} 0%, ${alpha(stat.color, 0.05)} 100%)`,
                border: '1px solid',
                borderColor: alpha(stat.color, 0.3),
              }}
            >
              <CardContent sx={{ p: 2 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography variant="caption" sx={{ color: alpha('#fff', 0.6), textTransform: 'uppercase', fontSize: '0.7rem' }}>
                      {stat.label}
                    </Typography>
                    <Typography variant="h4" sx={{ color: stat.color, fontWeight: 700, mt: 0.5 }}>
                      {stat.value}
                    </Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: alpha(stat.color, 0.2), width: 40, height: 40 }}>
                    {React.cloneElement(stat.icon, { sx: { color: stat.color, fontSize: 20 } })}
                  </Avatar>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Table */}
      <Paper
        elevation={0}
        sx={{
          background: alpha('#0f1419', 0.8),
          backdropFilter: 'blur(20px)',
          border: '1px solid',
          borderColor: alpha('#00d4ff', 0.1),
          borderRadius: 2,
          overflow: 'hidden',
        }}
      >
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
            <CircularProgress sx={{ color: '#00d4ff' }} />
          </Box>
        ) : filteredStrategies.length === 0 ? (
          <Box sx={{ p: 6, textAlign: 'center' }}>
            <DashboardIcon sx={{ fontSize: 64, color: alpha('#fff', 0.1), mb: 2 }} />
            <Typography variant="h6" sx={{ color: alpha('#fff', 0.4), mb: 1 }}>
              No Strategies Found
            </Typography>
            <Typography variant="body2" sx={{ color: alpha('#fff', 0.3) }}>
              {searchQuery ? 'Try a different search term' : 'Register your first trading strategy'}
            </Typography>
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ '& th': { borderBottom: `1px solid ${alpha('#00d4ff', 0.1)}` } }}>
                    <TableCell sx={{ color: alpha('#00d4ff', 0.8), fontWeight: 600, fontSize: '0.75rem' }}>Strategy ID</TableCell>
                    <TableCell sx={{ color: alpha('#00d4ff', 0.8), fontWeight: 600, fontSize: '0.75rem' }}>Status</TableCell>
                    <TableCell sx={{ color: alpha('#00d4ff', 0.8), fontWeight: 600, fontSize: '0.75rem' }}>Symbol</TableCell>
                    <TableCell sx={{ color: alpha('#00d4ff', 0.8), fontWeight: 600, fontSize: '0.75rem' }}>Timeframe</TableCell>
                    <TableCell sx={{ color: alpha('#00d4ff', 0.8), fontWeight: 600, fontSize: '0.75rem' }}>Progress</TableCell>
                    <TableCell sx={{ color: alpha('#00d4ff', 0.8), fontWeight: 600, fontSize: '0.75rem' }}>Signals</TableCell>
                    <TableCell sx={{ color: alpha('#00d4ff', 0.8), fontWeight: 600, fontSize: '0.75rem' }}>Last Signal</TableCell>
                    <TableCell sx={{ color: alpha('#00d4ff', 0.8), fontWeight: 600, fontSize: '0.75rem' }}>Active</TableCell>
                    <TableCell sx={{ color: alpha('#00d4ff', 0.8), fontWeight: 600, fontSize: '0.75rem' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <AnimatePresence>
                    {paginatedStrategies.map((strategy, index) => (
                      <motion.tr
                        key={strategy.strategy_id}
                        component={TableRow}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 20 }}
                        transition={{ delay: index * 0.03 }}
                        onClick={() => handleRowClick(strategy)}
                        sx={{
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          '&:hover': {
                            bgcolor: alpha('#00d4ff', 0.05),
                            '& td': {
                              borderColor: alpha('#00d4ff', 0.2),
                            },
                          },
                          '& td': {
                            borderBottom: `1px solid ${alpha('#00d4ff', 0.05)}`,
                            py: 2,
                          },
                        }}
                      >
                        <TableCell>
                          <Typography sx={{ color: '#fff', fontWeight: 600, fontSize: '0.875rem' }}>
                            {strategy.strategy_id}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={getStatusLabel(strategy)}
                            size="small"
                            sx={{
                              bgcolor: alpha(getStatusColor(strategy), 0.2),
                              color: getStatusColor(strategy),
                              border: '1px solid',
                              borderColor: alpha(getStatusColor(strategy), 0.4),
                              fontSize: '0.7rem',
                              fontWeight: 600,
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={strategy.symbol}
                            size="small"
                            sx={{
                              bgcolor: alpha('#00d4ff', 0.1),
                              color: '#00d4ff',
                              fontSize: '0.7rem',
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography sx={{ color: alpha('#fff', 0.7), fontSize: '0.875rem' }}>
                            {strategy.timeframe}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ width: 150 }}>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <LinearProgress
                              variant="determinate"
                              value={getConditionProgress(strategy)}
                              sx={{
                                flex: 1,
                                height: 6,
                                borderRadius: 3,
                                bgcolor: alpha('#00d4ff', 0.1),
                                '& .MuiLinearProgress-bar': {
                                  bgcolor: getStatusColor(strategy),
                                  borderRadius: 3,
                                },
                              }}
                            />
                            <Typography sx={{ color: alpha('#fff', 0.6), fontSize: '0.75rem', minWidth: 35 }}>
                              {getConditionProgress(strategy)}%
                            </Typography>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Typography sx={{ color: '#00d4ff', fontWeight: 600, fontSize: '0.875rem' }}>
                            {strategy.totalSignals || 0}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {strategy.lastSignal ? (
                            <Stack spacing={0.5}>
                              <Typography sx={{ color: alpha('#fff', 0.8), fontSize: '0.75rem', textTransform: 'uppercase' }}>
                                {strategy.lastSignal}
                              </Typography>
                              {strategy.lastSignalTime && (
                                <Typography sx={{ color: alpha('#fff', 0.4), fontSize: '0.65rem' }}>
                                  {new Date(strategy.lastSignalTime).toLocaleTimeString()}
                                </Typography>
                              )}
                            </Stack>
                          ) : (
                            <Typography sx={{ color: alpha('#fff', 0.3), fontSize: '0.75rem' }}>—</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Switch
                            checked={strategy.is_active}
                            onClick={(e) => toggleStrategy(strategy.strategy_id, strategy.is_active, e)}
                            size="small"
                            sx={{
                              '& .MuiSwitch-switchBase.Mui-checked': {
                                color: '#00ff88',
                              },
                              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                backgroundColor: '#00ff88',
                              },
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={0.5}>
                            <Tooltip title="Reset">
                              <IconButton
                                size="small"
                                onClick={(e) => resetStrategy(strategy.strategy_id, e)}
                                sx={{
                                  color: '#ffa502',
                                  '&:hover': { bgcolor: alpha('#ffa502', 0.1) },
                                }}
                              >
                                <RefreshIcon sx={{ fontSize: 18 }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setStrategyToDelete(strategy);
                                  setDeleteDialogOpen(true);
                                }}
                                sx={{
                                  color: '#ff4757',
                                  '&:hover': { bgcolor: alpha('#ff4757', 0.1) },
                                }}
                              >
                                <DeleteIcon sx={{ fontSize: 18 }} />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </motion.tr>
                    ))}
                  </AnimatePresence>
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={filteredStrategies.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 25, 50]}
              sx={{
                borderTop: `1px solid ${alpha('#00d4ff', 0.1)}`,
                color: alpha('#fff', 0.7),
                '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                  color: alpha('#fff', 0.7),
                },
                '& .MuiTablePagination-select': {
                  color: '#fff',
                },
                '& .MuiTablePagination-actions button': {
                  color: alpha('#fff', 0.7),
                  '&:hover': {
                    bgcolor: alpha('#00d4ff', 0.1),
                  },
                },
              }}
            />
          </>
        )}
      </Paper>

      {/* Enhanced Detail Dialog */}
      <StrategyDetailDialog
        open={detailDialogOpen}
        strategy={selectedStrategy}
        signalHistory={signalHistory}
        loading={detailLoading}
        onClose={() => {
          setDetailDialogOpen(false);
          setSelectedStrategy(null);
          setSignalHistory([]);
        }}
        onReset={resetStrategy}
        onDelete={(strategy) => {
          setStrategyToDelete(strategy);
          setDeleteDialogOpen(true);
          setDetailDialogOpen(false);
        }}
        apiBaseUrl={apiBaseUrl}
      />

      {/* Delete Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        PaperProps={{
          sx: {
            bgcolor: '#1a1f35',
            border: '1px solid',
            borderColor: alpha('#ff4757', 0.3),
            borderRadius: 2,
            minWidth: 400,
          },
        }}
      >
        <DialogTitle sx={{ color: '#fff', pb: 1 }}>Delete Strategy</DialogTitle>
        <Divider sx={{ borderColor: alpha('#ff4757', 0.2) }} />
        <DialogContent sx={{ pt: 3 }}>
          <Typography sx={{ color: alpha('#fff', 0.8) }}>
            Are you sure you want to delete strategy <strong style={{ color: '#ff4757' }}>"{strategyToDelete?.strategy_id}"</strong>?
          </Typography>
          <Typography sx={{ color: alpha('#fff', 0.6), mt: 2, fontSize: '0.875rem' }}>
            This action cannot be undone. All historical data will be permanently removed.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            sx={{ color: alpha('#fff', 0.7) }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => deleteStrategy(strategyToDelete?.strategy_id)}
            variant="contained"
            sx={{
              bgcolor: '#ff4757',
              '&:hover': { bgcolor: '#ff3344' },
            }}
          >
            Delete Permanently
          </Button>
        </DialogActions>
      </Dialog>

      <style>
        {`
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.5; }
          }
        `}
      </style>
    </Box>
  );
};

// Enhanced Strategy Detail Dialog
const StrategyDetailDialog = ({ open, strategy, signalHistory, loading, onClose, onReset, onDelete }) => {
  const [activeTab, setActiveTab] = useState(0);

  if (!strategy) return null;

  // Extract all signal types from conditions
  const earlySignal = Object.values(strategy.conditions || {}).find(c => c.type === 'early_signal');
  const confirmationSignal = Object.values(strategy.conditions || {}).find(c => c.type === 'confirmation_signal');
  const exitSignal = Object.values(strategy.conditions || {}).find(c => c.type === 'exit_signal');

  const conditions = Object.entries(strategy.conditions || {});
  const conditionsMet = strategy.detailedState?.conditions_met || {};
  const signalTimestamps = strategy.detailedState?.signal_timestamps || {};

  // Timing calculations
  const minSeconds = (strategy.timing_requirements?.C1_to_C2_min_seconds || 0);
  const maxSeconds = (strategy.timing_requirements?.C1_to_C2_max_seconds || 0);
  const minMinutes = (minSeconds / 60).toFixed(2);
  const maxMinutes = (maxSeconds / 60).toFixed(2);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: '#0f1419',
          border: '1px solid',
          borderColor: alpha('#00d4ff', 0.2),
          borderRadius: 2,
          maxHeight: '90vh',
        },
      }}
    >
      {/* Header */}
      <DialogTitle sx={{ 
        bgcolor: alpha('#00d4ff', 0.05), 
        borderBottom: `1px solid ${alpha('#00d4ff', 0.1)}`,
        p: 3,
      }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" spacing={2} alignItems="center">
            <Avatar
              sx={{
                width: 56,
                height: 56,
                bgcolor: alpha('#00d4ff', 0.2),
                border: '2px solid',
                borderColor: alpha('#00d4ff', 0.4),
              }}
            >
              <AssessmentIcon sx={{ color: '#00d4ff', fontSize: 28 }} />
            </Avatar>
            <Box>
              <Typography variant="h5" sx={{ color: '#fff', fontWeight: 700, mb: 0.5 }}>
                {strategy.strategy_id}
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                <Chip label={strategy.symbol} size="small" sx={{ bgcolor: alpha('#00d4ff', 0.1), color: '#00d4ff', height: 22 }} />
                <Chip label={strategy.timeframe} size="small" sx={{ bgcolor: alpha('#ffa502', 0.1), color: '#ffa502', height: 22 }} />
                <Chip 
                  label={strategy.is_active ? 'Active' : 'Inactive'} 
                  size="small" 
                  icon={strategy.is_active ? <PlayIcon /> : <PauseIcon />}
                  sx={{ 
                    bgcolor: alpha(strategy.is_active ? '#00ff88' : '#666', 0.1), 
                    color: strategy.is_active ? '#00ff88' : '#666',
                    height: 22,
                  }} 
                />
                {strategy.entryConfirmed && (
                  <Chip 
                    label="Entry Confirmed" 
                    size="small" 
                    icon={<CheckIcon />}
                    sx={{ 
                      bgcolor: alpha('#00ff88', 0.2), 
                      color: '#00ff88',
                      height: 22,
                      fontWeight: 700,
                    }} 
                  />
                )}
              </Stack>
            </Box>
          </Stack>
          <IconButton onClick={onClose} sx={{ color: alpha('#fff', 0.7) }}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      {/* Tabs */}
      <Box sx={{ borderBottom: `1px solid ${alpha('#00d4ff', 0.1)}` }}>
        <Tabs 
          value={activeTab} 
          onChange={(e, v) => setActiveTab(v)}
          sx={{
            '& .MuiTab-root': {
              color: alpha('#fff', 0.6),
              '&.Mui-selected': {
                color: '#00d4ff',
              },
            },
            '& .MuiTabs-indicator': {
              bgcolor: '#00d4ff',
            },
          }}
        >
          <Tab icon={<InfoIcon />} iconPosition="start" label="Overview" />
          <Tab icon={<SignalIcon />} iconPosition="start" label="Signals & Conditions" />
          <Tab icon={<HistoryIcon />} iconPosition="start" label={`History (${signalHistory.length})`} />
        </Tabs>
      </Box>

      <DialogContent sx={{ p: 3 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress sx={{ color: '#00d4ff' }} />
          </Box>
        ) : (
          <>
            {/* Tab 0: Overview */}
            {activeTab === 0 && (
              <Stack spacing={3}>
                {/* Stats Grid */}
                <Grid container spacing={2}>
                  <Grid item xs={3}>
                    <Card sx={{ bgcolor: alpha('#00d4ff', 0.05), border: `1px solid ${alpha('#00d4ff', 0.2)}` }}>
                      <CardContent sx={{ p: 2, textAlign: 'center' }}>
                        <Typography variant="caption" sx={{ color: alpha('#fff', 0.6), textTransform: 'uppercase', fontSize: '0.7rem' }}>
                          Total Signals
                        </Typography>
                        <Typography variant="h4" sx={{ color: '#00d4ff', fontWeight: 700, mt: 0.5 }}>
                          {strategy.totalSignals || 0}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={3}>
                    <Card sx={{ bgcolor: alpha('#00ff88', 0.05), border: `1px solid ${alpha('#00ff88', 0.2)}` }}>
                      <CardContent sx={{ p: 2, textAlign: 'center' }}>
                        <Typography variant="caption" sx={{ color: alpha('#fff', 0.6), textTransform: 'uppercase', fontSize: '0.7rem' }}>
                          Entry Status
                        </Typography>
                        <Typography variant="h5" sx={{ color: '#00ff88', fontWeight: 700, mt: 0.5 }}>
                          {strategy.entryConfirmed ? 'CONFIRMED' : 'PENDING'}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={3}>
                    <Card sx={{ bgcolor: alpha('#ffa502', 0.05), border: `1px solid ${alpha('#ffa502', 0.2)}` }}>
                      <CardContent sx={{ p: 2, textAlign: 'center' }}>
                        <Typography variant="caption" sx={{ color: alpha('#fff', 0.6), textTransform: 'uppercase', fontSize: '0.7rem' }}>
                          Last Signal
                        </Typography>
                        <Typography variant="h6" sx={{ color: '#ffa502', fontWeight: 700, mt: 0.5, textTransform: 'uppercase' }}>
                          {strategy.lastSignal || '—'}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={3}>
                    <Card sx={{ bgcolor: alpha('#764ba2', 0.05), border: `1px solid ${alpha('#764ba2', 0.2)}` }}>
                      <CardContent sx={{ p: 2, textAlign: 'center' }}>
                        <Typography variant="caption" sx={{ color: alpha('#fff', 0.6), textTransform: 'uppercase', fontSize: '0.7rem' }}>
                          Progress
                        </Typography>
                        <Typography variant="h4" sx={{ color: '#764ba2', fontWeight: 700, mt: 0.5 }}>
                          {Object.values(conditionsMet).filter(Boolean).length}/{conditions.length}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>

                {/* Description */}
                {strategy.description && (
                  <Paper sx={{ bgcolor: alpha('#00d4ff', 0.05), border: `1px solid ${alpha('#00d4ff', 0.2)}`, p: 2 }}>
                    <Stack direction="row" spacing={1.5} alignItems="flex-start">
                      <InfoIcon sx={{ color: '#00d4ff', mt: 0.3 }} />
                      <Box>
                        <Typography variant="subtitle2" sx={{ color: '#00d4ff', fontWeight: 600, mb: 0.5 }}>
                          Strategy Description
                        </Typography>
                        <Typography variant="body2" sx={{ color: alpha('#fff', 0.8), lineHeight: 1.6 }}>
                          {strategy.description}
                        </Typography>
                      </Box>
                    </Stack>
                  </Paper>
                )}

                {/* Signal Flow */}
                <Paper sx={{ bgcolor: alpha('#000', 0.3), border: `1px solid ${alpha('#00d4ff', 0.1)}`, p: 2 }}>
                  <Typography variant="h6" sx={{ color: '#fff', mb: 2, fontSize: '1rem', fontWeight: 600 }}>
                    Signal Flow
                  </Typography>
                  <Stack direction="row" spacing={2} alignItems="center">
                    {earlySignal && (
                      <>
                        <Chip
                          label={earlySignal.signal || 'Early Signal'}
                          icon={<WarningIcon />}
                          sx={{
                            bgcolor: alpha('#ffa502', 0.2),
                            color: '#ffa502',
                            fontWeight: 600,
                            fontSize: '0.85rem',
                            px: 1,
                          }}
                        />
                        <Typography sx={{ color: alpha('#fff', 0.5) }}>→</Typography>
                      </>
                    )}
                    {confirmationSignal && (
                      <>
                        <Chip
                          label={confirmationSignal.signal || 'Confirmation'}
                          icon={<CheckIcon />}
                          sx={{
                            bgcolor: alpha('#00ff88', 0.2),
                            color: '#00ff88',
                            fontWeight: 600,
                            fontSize: '0.85rem',
                            px: 1,
                          }}
                        />
                        <Typography sx={{ color: alpha('#fff', 0.5) }}>→</Typography>
                      </>
                    )}
                    {exitSignal && (
                      <Chip
                        label={exitSignal.signal || 'Exit Signal'}
                        icon={<TrendingDownIcon />}
                        sx={{
                          bgcolor: alpha('#ff4757', 0.2),
                          color: '#ff4757',
                          fontWeight: 600,
                          fontSize: '0.85rem',
                          px: 1,
                        }}
                      />
                    )}
                  </Stack>
                </Paper>

                {/* Timing Configuration */}
                <Paper sx={{ bgcolor: alpha('#000', 0.3), border: `1px solid ${alpha('#00d4ff', 0.1)}`, p: 2 }}>
                  <Typography variant="h6" sx={{ color: '#fff', mb: 2, fontSize: '1rem', fontWeight: 600 }}>
                    Timing Configuration
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Stack spacing={1}>
                        <Typography variant="caption" sx={{ color: alpha('#fff', 0.5), textTransform: 'uppercase', fontSize: '0.7rem' }}>
                          Minimum Wait Time
                        </Typography>
                        <Stack direction="row" alignItems="baseline" spacing={1}>
                          <Typography variant="h4" sx={{ color: '#00ff88', fontWeight: 700 }}>
                            {minMinutes}
                          </Typography>
                          <Typography variant="body2" sx={{ color: alpha('#fff', 0.6) }}>
                            minutes ({minSeconds}s)
                          </Typography>
                        </Stack>
                      </Stack>
                    </Grid>
                    <Grid item xs={6}>
                      <Stack spacing={1}>
                        <Typography variant="caption" sx={{ color: alpha('#fff', 0.5), textTransform: 'uppercase', fontSize: '0.7rem' }}>
                          Maximum Wait Time
                        </Typography>
                        <Stack direction="row" alignItems="baseline" spacing={1}>
                          <Typography variant="h4" sx={{ color: '#ff4757', fontWeight: 700 }}>
                            {maxMinutes}
                          </Typography>
                          <Typography variant="body2" sx={{ color: alpha('#fff', 0.6) }}>
                            minutes ({maxSeconds}s)
                          </Typography>
                        </Stack>
                      </Stack>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" sx={{ color: alpha('#fff', 0.5), textTransform: 'uppercase', fontSize: '0.7rem' }}>
                        Entry Logic
                      </Typography>
                      <Typography sx={{ color: '#00d4ff', fontWeight: 600, mt: 0.5 }}>
                        {strategy.entry_logic?.toUpperCase() || 'ALL'}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" sx={{ color: alpha('#fff', 0.5), textTransform: 'uppercase', fontSize: '0.7rem' }}>
                        Created At
                      </Typography>
                      <Typography sx={{ color: '#fff', fontWeight: 600, mt: 0.5 }}>
                        {strategy.created_at ? new Date(strategy.created_at).toLocaleString() : '—'}
                      </Typography>
                    </Grid>
                  </Grid>
                </Paper>
              </Stack>
            )}

            {/* Tab 1: Signals & Conditions */}
            {activeTab === 1 && (
              <Stack spacing={3}>
                {/* Conditions Progress */}
                <Paper sx={{ bgcolor: alpha('#000', 0.3), border: `1px solid ${alpha('#00d4ff', 0.1)}`, p: 2 }}>
                  <Typography variant="h6" sx={{ color: '#fff', mb: 2, fontSize: '1rem', fontWeight: 600 }}>
                    Condition Status ({Object.values(conditionsMet).filter(Boolean).length}/{conditions.length} Met)
                  </Typography>
                  <List sx={{ p: 0 }}>
                    {conditions.map(([conditionName, conditionConfig], index) => {
                      const isMet = conditionsMet[conditionName];
                      const timestamp = signalTimestamps[conditionName];
                      
                      return (
                        <React.Fragment key={conditionName}>
                          {index > 0 && <Divider sx={{ borderColor: alpha('#00d4ff', 0.05), my: 1.5 }} />}
                          <ListItem sx={{ px: 0, py: 1.5 }}>
                            <Stack direction="row" spacing={2} alignItems="flex-start" sx={{ width: '100%' }}>
                              <Avatar
                                sx={{
                                  width: 40,
                                  height: 40,
                                  bgcolor: alpha(isMet ? '#00ff88' : '#666', 0.2),
                                  border: '2px solid',
                                  borderColor: alpha(isMet ? '#00ff88' : '#666', 0.4),
                                }}
                              >
                                {isMet ? (
                                  <CheckIcon sx={{ color: '#00ff88', fontSize: 22 }} />
                                ) : (
                                  <UncheckedIcon sx={{ color: '#666', fontSize: 22 }} />
                                )}
                              </Avatar>
                              <Box sx={{ flex: 1 }}>
                                <Typography sx={{ color: '#fff', fontWeight: 600, fontSize: '1rem', mb: 1 }}>
                                  {conditionName}
                                </Typography>
                                <Grid container spacing={1}>
                                  <Grid item xs={12} sm={6}>
                                    <Typography variant="caption" sx={{ color: alpha('#fff', 0.5), fontSize: '0.7rem' }}>
                                      Signal Name
                                    </Typography>
                                    <Typography sx={{ color: '#00d4ff', fontWeight: 600, fontSize: '0.85rem' }}>
                                      {conditionConfig.signal || 'N/A'}
                                    </Typography>
                                  </Grid>
                                  <Grid item xs={12} sm={6}>
                                    <Typography variant="caption" sx={{ color: alpha('#fff', 0.5), fontSize: '0.7rem' }}>
                                      Type
                                    </Typography>
                                    <Typography sx={{ color: '#ffa502', fontWeight: 600, fontSize: '0.85rem' }}>
                                      {conditionConfig.type?.replace('_', ' ').toUpperCase() || 'N/A'}
                                    </Typography>
                                  </Grid>
                                  {timestamp && (
                                    <Grid item xs={12}>
                                      <Typography variant="caption" sx={{ color: alpha('#fff', 0.5), fontSize: '0.7rem' }}>
                                        Received At
                                      </Typography>
                                      <Typography sx={{ color: '#00ff88', fontWeight: 600, fontSize: '0.85rem' }}>
                                        {new Date(timestamp).toLocaleString()}
                                      </Typography>
                                    </Grid>
                                  )}
                                </Grid>
                              </Box>
                              <Chip
                                label={isMet ? 'MET' : 'PENDING'}
                                size="small"
                                sx={{
                                  bgcolor: alpha(isMet ? '#00ff88' : '#666', 0.2),
                                  color: isMet ? '#00ff88' : '#666',
                                  border: '1px solid',
                                  borderColor: alpha(isMet ? '#00ff88' : '#666', 0.4),
                                  fontWeight: 700,
                                  fontSize: '0.75rem',
                                  mt: 0.5,
                                }}
                              />
                            </Stack>
                          </ListItem>
                        </React.Fragment>
                      );
                    })}
                  </List>
                </Paper>
              </Stack>
            )}

            {/* Tab 2: History */}
            {activeTab === 2 && (
              <Paper sx={{ bgcolor: alpha('#000', 0.3), border: `1px solid ${alpha('#00d4ff', 0.1)}`, p: 2 }}>
                <Typography variant="h6" sx={{ color: '#fff', mb: 2, fontSize: '1rem', fontWeight: 600 }}>
                  Signal History (Last 50)
                </Typography>
                {signalHistory.length === 0 ? (
                  <Box sx={{ py: 4, textAlign: 'center' }}>
                    <HistoryIcon sx={{ fontSize: 48, color: alpha('#fff', 0.1), mb: 1 }} />
                    <Typography sx={{ color: alpha('#fff', 0.4) }}>
                      No signal history yet
                    </Typography>
                  </Box>
                ) : (
                  <List sx={{ p: 0, maxHeight: 400, overflow: 'auto' }}>
                    {signalHistory.map((signal, index) => (
                      <React.Fragment key={signal.id || index}>
                        {index > 0 && <Divider sx={{ borderColor: alpha('#00d4ff', 0.05), my: 1 }} />}
                        <ListItem sx={{ px: 0, py: 1.5 }}>
                          <Stack direction="row" spacing={2} alignItems="center" sx={{ width: '100%' }}>
                            <Avatar
                              sx={{
                                width: 32,
                                height: 32,
                                bgcolor: alpha('#00d4ff', 0.2),
                                fontSize: '0.75rem',
                                fontWeight: 700,
                              }}
                            >
                              #{signalHistory.length - index}
                            </Avatar>
                            <Box sx={{ flex: 1 }}>
                              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                                <Chip
                                  label={signal.signal_type || signal.condition_type}
                                  size="small"
                                  sx={{
                                    bgcolor: alpha('#00d4ff', 0.1),
                                    color: '#00d4ff',
                                    fontSize: '0.7rem',
                                    height: 20,
                                  }}
                                />
                                {signal.price && (
                                  <Chip
                                    label={`$${parseFloat(signal.price).toFixed(2)}`}
                                    size="small"
                                    sx={{
                                      bgcolor: alpha('#ffa502', 0.1),
                                      color: '#ffa502',
                                      fontSize: '0.7rem',
                                      height: 20,
                                    }}
                                  />
                                )}
                              </Stack>
                              <Typography variant="caption" sx={{ color: alpha('#fff', 0.6), fontSize: '0.75rem' }}>
                                {new Date(signal.signal_timestamp || signal.detected_at).toLocaleString()}
                              </Typography>
                            </Box>
                          </Stack>
                        </ListItem>
                      </React.Fragment>
                    ))}
                  </List>
                )}
              </Paper>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 0, gap: 1, borderTop: `1px solid ${alpha('#00d4ff', 0.1)}` }}>
        <Button
          startIcon={<RefreshIcon />}
          onClick={(e) => {
            onReset(strategy.strategy_id);
            e.stopPropagation();
          }}
          sx={{
            color: '#ffa502',
            borderColor: alpha('#ffa502', 0.5),
            '&:hover': {
              borderColor: '#ffa502',
              bgcolor: alpha('#ffa502', 0.1),
            },
          }}
          variant="outlined"
        >
          Reset Strategy
        </Button>
        <Button
          startIcon={<DeleteIcon />}
          onClick={() => onDelete(strategy)}
          sx={{
            color: '#ff4757',
            borderColor: alpha('#ff4757', 0.5),
            '&:hover': {
              borderColor: '#ff4757',
              bgcolor: alpha('#ff4757', 0.1),
            },
          }}
          variant="outlined"
        >
          Delete
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button
          onClick={onClose}
          variant="contained"
          sx={{
            bgcolor: '#00d4ff',
            color: '#000',
            fontWeight: 600,
            '&:hover': { bgcolor: '#00b8ff' },
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MultiStrategyDashboard;
